import React, { createContext, useContext, useEffect, useState } from "react";
import { authService } from "../lib/authService";
import { profileService } from "../lib/profileService";

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      console.log("[AUTH] Initializing auth...");
      const savedToken = localStorage.getItem("vita_token");
      const savedUser = localStorage.getItem("vita_user");

      if (savedToken && savedUser) {
        console.log("[AUTH] Found saved session");
        const parsedUser = JSON.parse(savedUser);
        const isValid = await authService.validateToken(savedToken);
        if (isValid) {
          console.log("[AUTH] Token is valid");
          setSession({ access_token: savedToken, user: parsedUser });
          setUser(parsedUser);
          await loadUserProfile(parsedUser.id);
          return;
        }
      }

      console.log("[AUTH] No valid session found");
      localStorage.removeItem("vita_token");
      localStorage.removeItem("vita_user");
      setSession(null);
      setUser(null);
      setUserProfile(null);
      setLoading(false);
    };

    initAuth();
  }, []);

  const loadUserProfile = async () => {
    try {
      console.log("[PROFILE] Loading user profile...");
      const profile = await profileService.getProfile();
      console.log("[PROFILE] Profile loaded:", profile);

      if (profile) {
        console.log("[PROFILE] profile_completed:", profile.profile_completed);
        setUserProfile(profile);
      } else {
        // New user without profile yet
        console.log(
          "[PROFILE] No profile found, setting default incomplete profile",
        );
        setUserProfile({
          profile_completed: false,
          id: null,
          username: "",
          company_name: "",
          contact_number: "",
        });
      }
    } catch (error) {
      console.error("Error loading profile:", error);
      console.log("[PROFILE] Using default incomplete profile");
      setUserProfile({
        profile_completed: false,
        id: null,
        username: "",
        company_name: "",
        contact_number: "",
      });
    } finally {
      console.log("[PROFILE] Setting loading to false");
      setLoading(false);
    }
  };

  const signInWithGoogle = () => {
    console.log("[AUTH] Opening login modal");
    setShowLoginModal(true);
  };

  const handleLoginModalSubmit = async (credentials) => {
    try {
      console.log("[AUTH] Starting sign in flow...");
      const { email, password, name } = credentials;

      let authResponse;
      try {
        console.log("[AUTH] Attempting register...");
        await authService.register({ email, password, name });
        console.log("[AUTH] Register succeeded");
      } catch (error) {
        console.log(
          "[AUTH] Register failed (expected if user exists):",
          error.message,
        );
        // Se ja existir ou falhar por outro motivo, ainda tentamos login.
      }

      console.log("[AUTH] Attempting login...");
      authResponse = await authService.login({ email, password });
      console.log("[AUTH] Login succeeded:", authResponse);

      localStorage.setItem("vita_token", authResponse.access_token);
      localStorage.setItem("vita_user", JSON.stringify(authResponse.user));
      console.log("[AUTH] Token saved to localStorage");

      setSession({
        access_token: authResponse.access_token,
        user: authResponse.user,
      });
      setUser(authResponse.user);
      console.log("[AUTH] State updated, loading profile...");

      await loadUserProfile();
      console.log("[AUTH] Profile loaded, sign in complete");
    } catch (error) {
      console.error("Sign-in error:", error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      localStorage.removeItem("vita_token");
      localStorage.removeItem("vita_user");
      setSession(null);
      setUser(null);
      setUserProfile(null);
    } catch (error) {
      console.error("Sign out error:", error);
      throw error;
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await loadUserProfile();
    }
  };

  const setProfileCompleted = (profile) => {
    console.log("[AUTH] Setting profile directly:", profile);
    setUserProfile(profile);
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        userProfile,
        loading,
        signInWithGoogle,
        signOut,
        refreshProfile,
        setProfileCompleted,
        showLoginModal,
        setShowLoginModal,
        handleLoginModalSubmit,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
